import React from "react";

import { faLightbulbOn } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import NoteCheckbox from "~/common/NoteCheckbox";
import { useMedia } from "~/lib/hooks";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { IconButton } from "~/ui/legacyFellowIndex";

import styles from "./EmployeeGuidance.module.css";

export interface Suggestion {
	id: string;
	topic: string;
	title: string;
	promptManager: string | null;
	promptReport: string | null;
	creator: UserAttrs | null;
}

type EmployeeGuidanceProps = {
	guidance: Suggestion;
	onAdd?: (topic: Suggestion) => void;
	onDismiss?: (topic: Suggestion) => void;
	showManager?: boolean;
	className?: string;
};

export default function EmployeeGuidance({
	guidance,
	onAdd,
	onDismiss,
	showManager,
	className = undefined,
}: EmployeeGuidanceProps): React.ReactElement {
	const [hidden, setHidden] = React.useState(false);
	const [collapsed, setCollapsed] = React.useState(true);
	const onAcknowledgeClicked = React.useCallback(() => {
		setHidden(true);
		onAdd && onAdd(guidance);
	}, [onAdd, guidance]);
	const onDismissClicked = React.useCallback(() => {
		if (!onDismiss) return;
		setHidden(true);
		onDismiss(guidance);
	}, [onDismiss, guidance]);

	// @media (--max-sm)
	const isSmallScreen = useMedia("(max-width: 479px)");

	const toggleCollapsed = React.useCallback(() => {
		setCollapsed(!collapsed);
	}, [collapsed]);

	if (hidden) return <></>;

	return (
		<div className={cx(styles.employeeGuidance, className)}>
			<div className={styles.description}>
				<div className={styles.topic}>
					<span>
						<FontAwesomeIcon icon={faLightbulbOn} fixedWidth className={styles.lightBulbOn} />
						How to discuss this topic {!isSmallScreen && `with your ${showManager ? "direct report" : "manager"}`}
					</span>
					{isSmallScreen && (
						<IconButton
							icon={faChevronDown}
							onClick={toggleCollapsed}
							className={cx(styles.collapseButton, { [styles.collapseButtonCollapsed]: collapsed })}
						/>
					)}
				</div>
				{(!collapsed || !isSmallScreen) && (
					<>
						<p className={styles.content}>{showManager ? guidance.promptManager : guidance.promptReport}</p>
						<div className={styles.attribution}>
							{guidance.creator && (
								<>
									<Avatar user={guidance.creator} /> <strong>{guidance.creator.fullName}</strong>
									&nbsp;
									{guidance.creator.title && `- ${guidance.creator.title}`}
								</>
							)}
						</div>
					</>
				)}
			</div>

			<div className={styles.meta}>
				<div className={styles.talkingPointContainer}>
					<NoteCheckbox
						type="talking-point"
						state="unchecked"
						readOnly
						checkboxSize="large"
						className={styles.talkingPointCheckbox}
					/>
					<span className={styles.talkingPoint}>{guidance.topic}</span>
				</div>
				<div className={styles.metaButtonContainer}>
					{onDismiss && (
						<Button className={styles.addOrDismissButton} size="normal" onClick={onDismissClicked}>
							Dismiss
						</Button>
					)}
					{onAdd && (
						<Button
							className={styles.addOrDismissButton}
							variant="primary"
							size="normal"
							onClick={onAcknowledgeClicked}
						>
							Add to 1-on-1
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
